import { useEffect, useRef, useState, type SyntheticEvent } from 'react'
import { BookOpen, CircleHelp, Clapperboard, Film as FilmIcon, LayoutList, Music2, Newspaper, Podcast, RadioTower, Shapes, Tags, Trophy, Tv, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { ContentTag, ContentType, Film } from '../lib/api'

/** Mutually exclusive basic forms. */
export const CONTENT_TYPES: ContentType[] = [
  'MOVIE', 'SERIES', 'TV_SHOW', 'PODCAST', 'LIVE_STREAM', 'OTHER', 'UNKNOWN',
]

/** Non-exclusive editorial facets. */
export const CONTENT_TAGS: ContentTag[] = [
  'DOCUMENTARY', 'NEWS', 'MAGAZINE', 'SPORT', 'MUSIC', 'CHILDREN', 'KNOWLEDGE',
]

export const CONTENT_TYPE_ICONS: Record<ContentType, typeof FilmIcon> = {
  MOVIE: FilmIcon, SERIES: Clapperboard, DOCUMENTARY: BookOpen, NEWS: Newspaper,
  MAGAZINE: LayoutList, TV_SHOW: Tv, SPORT: Trophy, CHILDREN: CircleHelp,
  MUSIC: Music2, KNOWLEDGE: CircleHelp, PODCAST: Podcast,
  LIVE_STREAM: RadioTower, OTHER: Shapes, UNKNOWN: CircleHelp,
}

export const CONTENT_TAG_ICONS: Record<ContentTag, typeof FilmIcon> = {
  DOCUMENTARY: BookOpen, NEWS: Newspaper, MAGAZINE: LayoutList, SPORT: Trophy,
  MUSIC: Music2, CHILDREN: CircleHelp, KNOWLEDGE: BookOpen,
}

function primaryType(value: ContentType): ContentType {
  return CONTENT_TYPES.includes(value) ? value : 'UNKNOWN'
}

export function ContentTypeSelect({ film, compact = false, disabled = false, onChange }: {
  film: Film
  compact?: boolean
  disabled?: boolean
  onChange: (contentType: ContentType, tags: ContentTag[]) => void
}) {
  const { t } = useTranslation()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const classification = film.classification
  const value = primaryType(classification?.type ?? 'UNKNOWN')
  const selectedTags = classification?.tags ?? []
  const TypeIcon = CONTENT_TYPE_ICONS[value]
  const stop = (event: SyntheticEvent) => event.stopPropagation()

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }
    window.addEventListener('resize', close)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const rect = open ? buttonRef.current?.getBoundingClientRect() : null
  const tagNames = selectedTags.map((tag) => t(`sendungen.classification.types.${tag}`)).join(', ')
  const toggleTag = (tag: ContentTag) => onChange(value, selectedTags.includes(tag)
    ? selectedTags.filter((item) => item !== tag)
    : [...selectedTags, tag])

  return <div className={`classification-picker ${compact ? 'compact' : ''} ${classification?.source === 'MANUAL' ? 'manual' : ''}`} onClick={stop} onPointerDown={stop}>
    {!compact && <span>{t('sendungen.classification.label')}</span>}
    <button
      ref={buttonRef}
      type="button"
      className="classification-trigger"
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`${t('sendungen.classification.change')}: ${t(`sendungen.classification.types.${value}`)}`}
      title={`${t(`sendungen.classification.types.${value}`)}${tagNames ? ` · ${tagNames}` : ''} · ${t('sendungen.classification.tooltip', { confidence: classification?.confidence ?? 0 })}`}
      onClick={() => setOpen((current) => !current)}
    >
      {compact
        ? <span className="classification-compact-icons" aria-hidden="true">
            <TypeIcon className="classification-primary-icon" />
            {selectedTags.length > 0 && <span className="classification-secondary-icons">
              {selectedTags.slice(0, 3).map((tag) => {
                const TagIcon = CONTENT_TAG_ICONS[tag]
                return <TagIcon className={`classification-secondary-icon tag-${tag.toLowerCase()}`} key={tag} />
              })}
            </span>}
          </span>
        : <TypeIcon aria-hidden="true" />}
      {!compact && <span className="classification-trigger-copy"><strong>{t(`sendungen.classification.types.${value}`)}</strong>{selectedTags.length > 0 && <small><Tags />{tagNames}</small>}</span>}
      {classification?.source === 'MANUAL' && <span className="classification-corrected-dot" aria-label={t('sendungen.classification.corrected')} />}
    </button>
    {open && rect && createPortal(<>
      <button className="classification-popover-backdrop" type="button" aria-label={t('common.close')} onClick={() => setOpen(false)} />
      <div className="classification-popover" role="dialog" aria-label={t('sendungen.classification.chooseType')} onClick={stop} onPointerDown={stop} onWheel={stop} style={{ left: Math.max(8, Math.min(rect.left, window.innerWidth - 318)), top: Math.max(8, Math.min(rect.bottom + 7, window.innerHeight - 558)) }}>
        <header><div><strong>{t('sendungen.classification.chooseType')}</strong><small>{t('sendungen.classification.chooseTypeHint')}</small></div><button type="button" onClick={() => setOpen(false)} aria-label={t('common.close')}><X /></button></header>
        <section className="classification-section">
          <h4>{t('sendungen.classification.primaryForm')}</h4>
          <div className="classification-options">
            {CONTENT_TYPES.map((type) => {
              const Icon = CONTENT_TYPE_ICONS[type]
              return <button type="button" role="radio" aria-checked={value === type} className={value === type ? 'active' : ''} key={type} onClick={() => { if (type !== value) onChange(type, selectedTags); setOpen(false) }}>
                <Icon /><span><strong>{t(`sendungen.classification.types.${type}`)}</strong><small>{t(`sendungen.classification.typeHints.${type}`)}</small></span>
              </button>
            })}
          </div>
        </section>
        <section className="classification-section tag-section">
          <h4>{t('sendungen.classification.additionalTags')}</h4>
          <p>{t('sendungen.classification.additionalTagsHint')}</p>
          <div className="classification-tag-options">
            {CONTENT_TAGS.map((tag) => {
              const Icon = CONTENT_TAG_ICONS[tag]
              const checked = selectedTags.includes(tag)
              return <button type="button" role="checkbox" aria-checked={checked} className={checked ? 'active' : ''} key={tag} onClick={() => toggleTag(tag)}><Icon /><span>{t(`sendungen.classification.types.${tag}`)}</span></button>
            })}
          </div>
        </section>
      </div>
    </>, document.body)}
  </div>
}
